/**
 * AI Council hooks — typed TanStack Query wrappers for the LEGACY
 * multi-provider council (/api/council/*, backend/routers/council.py).
 *
 * Types below mirror the REAL wire shapes (CouncilVerdictResponse /
 * MembersResponse pydantic models) — not the aspirational CouncilVerdict in
 * sdk/types/ai.ts, which describes a richer interface the backend does not
 * serve on this route.
 */

import { useMutation, useQuery } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api-client";

export const COUNCIL_QUERY_KEYS = {
  members: ["council", "members"] as const,
  verdict: (question: string) => ["council", "verdict", question] as const,
};

/** One provider's answer (CouncilMemberResponse, council.py). */
export interface CouncilMemberWire {
  name: string;
  model: string;
  response: string;
  confidence: number;
  reasoning: string;
  search_used: boolean;
  error?: string | null;
}

/** POST /api/council/ask response (CouncilVerdictResponse, council.py). */
export interface CouncilVerdictWire {
  question: string;
  consensus_answer: string;
  confidence: number;
  dissenting_views: string[];
  key_points: string[];
  search_context?: string | null;
  member_responses: CouncilMemberWire[];
  timestamp: string;
}

/** GET /api/council/members response (MembersResponse, council.py). */
export interface CouncilMembersWire {
  members: { name: string; model: string; provider: string }[];
  search: { name: string; model: string; role: string };
  judge: { name: string; model: string; role: string };
}

/**
 * Ask the AI council a question (fires a PAID multi-provider call).
 * Backend accepts question (min 5 chars), use_search, openai_model.
 */
export function useCouncilAsk() {
  return useMutation({
    mutationFn: (params: { question: string; use_search?: boolean }) =>
      apiPost<CouncilVerdictWire>("/api/council/ask", params),
  });
}

/** Fetch the council roster (providers + search + judge seats). */
export function useCouncilMembers() {
  return useQuery({
    queryKey: COUNCIL_QUERY_KEYS.members,
    queryFn: () => apiGet<CouncilMembersWire>("/api/council/members"),
    staleTime: 600_000,
  });
}
